'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';


interface Service {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface ServiceSpecificAnswers {
  // Window Cleaning
  windowType?: string;
  skylights?: string;
  windowStories?: string;
  
  // Power Washing
  powerWashAreas?: string[];
  powerWashOther?: string;
  mossBuildup?: string;
  
  // Roof Cleaning
  roofType?: string;
  roofPitch?: string;
  roofStories?: string;
  
  // Gutter Cleaning
  gutterRoofPitch?: string;
  gutterStories?: string;
}

interface FormData {
  // Basic Information
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
  
  // Property Details
  homeSize: string;
  homeType: string;
  stories: string;
  
  // Service Selection
  services: string[];
  
  // Service-Specific Answers
  serviceAnswers: Record<string, ServiceSpecificAnswers>;
  
  additionalNotes: string;
  
  // Promo Code and Scheduling
  promoCode?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  visitDate?: string;
  visitTime?: string;
}

interface QuoteFormStepsProps {
  onSubmit: (data: FormData) => void;
}

const QuoteFormSteps = ({ onSubmit }: QuoteFormStepsProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    homeSize: '',
    homeType: '',
    stories: '',
    services: [],
    serviceAnswers: {},
    additionalNotes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const totalSteps = 5;

  // Local storage key
  const STORAGE_KEY = 'crystal_clean_quote_form_data';

  // Load saved form data on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData.formData || formData);
        setCurrentStep(parsedData.currentStep || 1);
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
    }
  }, []);

  // Save form data to local storage whenever it changes
  useEffect(() => {
    try {
      const dataToSave = {
        formData,
        currentStep,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [formData, currentStep]);

  const services: Service[] = [
    {
      id: 'window',
      name: 'Window Cleaning',
      description: 'Professional interior and exterior window cleaning',
      icon: 'SparklesIcon',
    },
    {
      id: 'powerwashing',
      name: 'Power Washing',
      description: 'Driveways, patios, walkways, and more',
      icon: 'HomeModernIcon',
    },
    {
      id: 'roof',
      name: 'Roof Cleaning (Soft Wash)',
      description: 'Gentle roof cleaning to remove moss and algae',
      icon: 'BuildingOfficeIcon',
    },
    {
      id: 'gutter',
      name: 'Gutter Cleaning',
      description: 'Complete gutter system cleaning and inspection',
      icon: 'HomeIcon',
    },
    {
      id: 'house-washing',
      name: 'House Washing (Exterior Siding)',
      description: 'Complete exterior siding cleaning',
      icon: 'HomeModernIcon',
    },
    {
      id: 'christmas-lights',
      name: 'Christmas Light Installation',
      description: 'Professional holiday lighting (lights supplied by Crystal Clean)',
      icon: 'LightBulbIcon',
    },
  ];

  // Pricing calculation engine
  const calculateServicePrice = (serviceId: string): { min: number; max: number } | null => {
    const answers = formData.serviceAnswers[serviceId] || {};
    const homeSize = formData.homeSize;

    // Home size increment mapping (0-5 for 6 categories)
    const homeSizeIncrements: { [key: string]: number } = {
      'size-2000-under': 0,
      'size-2001-3000': 1,
      'size-3001-4000': 2,
      'size-4001-5000': 3,
      'size-5001-6000': 4,
      'size-6000-plus': 5,
    };

    const increment = homeSizeIncrements[homeSize] || 0;

    // Story multiplier helper (1.1x per additional story above 1)
    const getStoryMultiplier = (stories?: string): number => {
      if (!stories) return 1;
      if (stories === 'One') return 1;
      if (stories === 'Two') return 1.1;
      if (stories === 'Three or more') return 1.21; // 1.1 * 1.1 for 2 additional stories
      return 1;
    };

    if (serviceId === 'window') {
      // Window Cleaning: Base $150 + $20 per increment
      let basePrice = 150 + (increment * 20);

      // Interior vs Exterior multiplier
      if (answers.windowType === 'Interior & exterior') {
        basePrice = basePrice * 2;
      }

      // Skylights: $10 per skylight
      let skylightCost = 0;
      if (answers.skylights === '1–2') skylightCost = 15; // Average 1.5 skylights
      if (answers.skylights === '3–5') skylightCost = 40; // Average 4 skylights
      if (answers.skylights === '6+') skylightCost = 70; // Average 7 skylights

      let total = basePrice + skylightCost;
      
      // Apply story multiplier
      const storyMultiplier = getStoryMultiplier(answers.windowStories);
      total = Math.round(total * storyMultiplier);
      
      return { min: total, max: Math.round(total * 1.2) };
    }

    if (serviceId === 'powerwashing') {
      // Power Washing: Driveway $250 + $25/increment, Walkways $125 + $25/increment, Patio $50 + $20/increment, Deck $125 + $25/increment
      const areas = answers.powerWashAreas || [];
      let total = 0;

      if (areas.includes('Driveway')) {
        total += 250 + (increment * 25);
      }
      if (areas.includes('Walkways')) {
        total += 125 + (increment * 25);
      }
      if (areas.includes('Patio')) {
        total += 50 + (increment * 20);
      }
      if (areas.includes('Deck')) {
        total += 125 + (increment * 25);
      }
      if (areas.includes('Stairs')) {
        total += 125 + (increment * 25);
      }

      // Apply moss multiplier if visible moss build-up
      if (answers.mossBuildup === 'Yes') {
        total = Math.round(total * 1.25);
      }

      if (total === 0) return null;
      return { min: total, max: Math.round(total * 1.2) };
    }

    if (serviceId === 'gutter') {
      // Gutter Cleaning: Base $150 + $20 per increment
      let basePrice = 150 + (increment * 20);

      // Roof pitch multipliers
      if (answers.gutterRoofPitch === 'Moderately steep') {
        basePrice = Math.round(basePrice * 1.15);
      } else if (answers.gutterRoofPitch === 'Steep') {
        basePrice = Math.round(basePrice * 1.25);
      }

      // Apply story multiplier
      const storyMultiplier = getStoryMultiplier(answers.gutterStories);
      basePrice = Math.round(basePrice * storyMultiplier);

      return { min: basePrice, max: Math.round(basePrice * 1.2) };
    }

    if (serviceId === 'roof') {
      // Roof Cleaning: Base $450 + $75 per increment
      let basePrice = 450 + (increment * 75);

      // Roof material multipliers
      if (answers.roofType === 'Tile') {
        basePrice = Math.round(basePrice * 1.35);
      } else if (answers.roofType === 'Metal' || answers.roofType === 'Wood') {
        basePrice = Math.round(basePrice * 1.30);
      }

      // Apply story multiplier
      const storyMultiplier = getStoryMultiplier(answers.roofStories);
      basePrice = Math.round(basePrice * storyMultiplier);

      return { min: basePrice, max: Math.round(basePrice * 1.2) };
    }

    if (serviceId === 'house-washing') {
      // Exterior Siding: Base $400 + $40 per increment
      let basePrice = 400 + (increment * 40);
      return { min: basePrice, max: Math.round(basePrice * 1.2) };
    }

    if (serviceId === 'christmas-lights') {
      // No pricing shown - consultation required
      return null;
    }

    return null;
  };

  const calculateEstimatedTotal = (): { min: number; max: number; hasChristmasLights: boolean } => {
    let minTotal = 0;
    let maxTotal = 0;
    let hasChristmasLights = false;

    formData.services.forEach((serviceId) => {
      if (serviceId === 'christmas-lights') {
        hasChristmasLights = true;
        return;
      }

      const pricing = calculateServicePrice(serviceId);
      if (pricing) {
        minTotal += pricing.min;
        maxTotal += pricing.max;
      }
    });

    return { min: minTotal, max: maxTotal, hasChristmasLights };
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
        newErrors.phone = 'Invalid phone format (e.g., 555-123-4567)';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.address.trim()) newErrors.address = 'Property address is required';
    }

    if (step === 2) {
      if (!formData.homeSize) newErrors.homeSize = 'Please select home size';
      if (!formData.homeType) newErrors.homeType = 'Please select home type';
      if (!formData.stories) newErrors.stories = 'Please select number of stories';
    }

    if (step === 3) {
      if (formData.services.length === 0) {
        newErrors.services = 'Please select at least one service';
      }
    }

    if (step === 4) {
      // Validate service-specific questions
      formData.services.forEach((serviceId) => {
        const answers = formData.serviceAnswers[serviceId] || {};
        
        if (serviceId === 'window') {
          if (!answers.windowType) newErrors[`${serviceId}_windowType`] = 'Please select window cleaning type';
          if (!answers.skylights) newErrors[`${serviceId}_skylights`] = 'Please select number of skylights';
          if (!answers.windowStories) newErrors[`${serviceId}_windowStories`] = 'Please select number of stories';
        }
        
        if (serviceId === 'powerwashing') {
          if (!answers.powerWashAreas || answers.powerWashAreas.length === 0) {
            newErrors[`${serviceId}_powerWashAreas`] = 'Please select at least one area';
          }
          if (!answers.mossBuildup) newErrors[`${serviceId}_mossBuildup`] = 'Please indicate if there is visible moss build-up';
        }
        
        if (serviceId === 'roof') {
          if (!answers.roofType) newErrors[`${serviceId}_roofType`] = 'Please select roof material';
          if (!answers.roofStories) newErrors[`${serviceId}_roofStories`] = 'Please select number of stories';
        }
        
        if (serviceId === 'gutter') {
          if (!answers.gutterRoofPitch) newErrors[`${serviceId}_gutterRoofPitch`] = 'Please select roof pitch';
          if (!answers.gutterStories) newErrors[`${serviceId}_gutterStories`] = 'Please select number of stories';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) {
      return;
    }
    
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Track step progression
      const stepNames = ['Contact Info', 'Property Details', 'Services', 'Service Details', 'Review'];
      analytics.trackQuoteStep(nextStep, stepNames[nextStep - 1] || `Step ${nextStep}`);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleServiceToggle = (serviceId: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter((id) => id !== serviceId)
        : [...prev.services, serviceId],
    }));
    setErrors((prev) => ({ ...prev, services: undefined }));
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleServiceAnswerChange = (serviceId: string, field: string, value: string | string[]) => {
    setFormData((prev) => ({
      ...prev,
      serviceAnswers: {
        ...prev.serviceAnswers,
        [serviceId]: {
          ...prev.serviceAnswers[serviceId],
          [field]: value,
        },
      },
    }));
    setErrors((prev) => ({ ...prev, [`${serviceId}_${field}`]: undefined }));
  };

  const handleSubmit = () => {
    if (validateStep(5)) {
      onSubmit(formData);
    }
  };

  const renderServiceSpecificQuestions = (serviceId: string) => {
    const answers = formData.serviceAnswers[serviceId] || {};

    switch (serviceId) {
      case 'window':
        return (
          <div className="space-y-4 mt-4 p-4 bg-accent/30 rounded-lg">
            <h4 className="font-semibold text-foreground">Window Cleaning Details</h4>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Are you looking for interior, exterior, or both? *
              </label>
              <div className="space-y-2">
                {['Exterior only', 'Interior only', 'Interior & exterior'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_windowType`}
                      value={option}
                      checked={answers.windowType === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'windowType', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_windowType`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_windowType`]}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                How many skylights are included? *
              </label>
              <div className="space-y-2">
                {['None', '1–2', '3–5', '6+'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_skylights`}
                      value={option}
                      checked={answers.skylights === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'skylights', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_skylights`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_skylights`]}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                How many stories is your home? *
              </label>
              <div className="space-y-2">
                {['One', 'Two', 'Three or more'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_windowStories`}
                      value={option}
                      checked={answers.windowStories === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'windowStories', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_windowStories`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_windowStories`]}</p>
              )}
            </div>
          </div>
        );

      case 'powerwashing':
        return (
          <div className="space-y-4 mt-4 p-4 bg-accent/30 rounded-lg">
            <h4 className="font-semibold text-foreground">Power Washing Details</h4>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                What areas would you like power washed? (Select all that apply) *
              </label>
              <div className="space-y-2">
                {['Driveway', 'Walkways', 'Patio', 'Deck', 'Stairs', 'Other'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      value={option}
                      checked={(answers.powerWashAreas || []).includes(option)}
                      onChange={(e) => {
                        const currentAreas = answers.powerWashAreas || [];
                        const newAreas = e.target.checked
                          ? [...currentAreas, option]
                          : currentAreas.filter((a) => a !== option);
                        handleServiceAnswerChange(serviceId, 'powerWashAreas', newAreas);
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_powerWashAreas`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_powerWashAreas`]}</p>
              )}
              
              {/* Text input for "Other" option */}
              {(answers.powerWashAreas || []).includes('Other') && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Please specify other area:
                  </label>
                  <input
                    type="text"
                    value={answers.powerWashOther || ''}
                    onChange={(e) => handleServiceAnswerChange(serviceId, 'powerWashOther', e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="e.g., Fence, Garage door, etc."
                  />
                  <p className="text-xs text-amber-600 mt-2 flex items-start">
                    <Icon name="ExclamationTriangleIcon" size={14} className="mr-1 mt-0.5 flex-shrink-0" />
                    <span>Custom requests are not included in the estimated quote below and will be assessed separately.</span>
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Is there visible moss build-up? *
              </label>
              <div className="space-y-2">
                {['Yes', 'No', 'Not sure'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_mossBuildup`}
                      value={option}
                      checked={answers.mossBuildup === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'mossBuildup', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_mossBuildup`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_mossBuildup`]}</p>
              )}
            </div>
          </div>
        );

      case 'roof':
        return (
          <div className="space-y-4 mt-4 p-4 bg-accent/30 rounded-lg">
            <h4 className="font-semibold text-foreground">Roof Cleaning Details</h4>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                What is the roof material? *
              </label>
              <div className="space-y-2">
                {['Asphalt / standard roofing', 'Tile', 'Wood', 'Metal', 'Not sure'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_roofType`}
                      value={option}
                      checked={answers.roofType === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'roofType', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_roofType`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_roofType`]}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                How many stories is your home? *
              </label>
              <div className="space-y-2">
                {['One', 'Two', 'Three or more'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_roofStories`}
                      value={option}
                      checked={answers.roofStories === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'roofStories', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_roofStories`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_roofStories`]}</p>
              )}
            </div>
          </div>
        );

      case 'gutter':
        return (
          <div className="space-y-4 mt-4 p-4 bg-accent/30 rounded-lg">
            <h4 className="font-semibold text-foreground">Gutter Cleaning Details</h4>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                What is the roof pitch? *
              </label>
              <div className="space-y-2">
                {['Standard pitch', 'Moderately steep', 'Steep'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_gutterRoofPitch`}
                      value={option}
                      checked={answers.gutterRoofPitch === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'gutterRoofPitch', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_gutterRoofPitch`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_gutterRoofPitch`]}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                How many stories is your home? *
              </label>
              <div className="space-y-2">
                {['One', 'Two', 'Three or more'].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${serviceId}_gutterStories`}
                      value={option}
                      checked={answers.gutterStories === option}
                      onChange={(e) => handleServiceAnswerChange(serviceId, 'gutterStories', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
              {errors[`${serviceId}_gutterStories`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${serviceId}_gutterStories`]}</p>
              )}
            </div>
          </div>
        );

      case 'house-washing':
        return (
          <div className="space-y-4 mt-4 p-4 bg-accent/30 rounded-lg">
            <h4 className="font-semibold text-foreground">House Washing Details</h4>
            <p className="text-sm text-muted-foreground">
              No additional questions required. Pricing is based on home size.
            </p>
          </div>
        );

      case 'christmas-lights':
        return (
          <div className="space-y-4 mt-4 p-4 bg-accent/30 rounded-lg">
            <h4 className="font-semibold text-foreground">Christmas Light Installation</h4>
            <p className="text-sm text-muted-foreground">
              No additional questions required. Pricing will be provided after an on-site assessment.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Track form abandonment on unmount
  useEffect(() => {
    return () => {
      if (currentStep < totalSteps && currentStep > 0) {
        const stepNames = ['Contact Info', 'Property Details', 'Services', 'Service Details', 'Review'];
        analytics.trackQuoteAbandonment(currentStep, stepNames[currentStep - 1] || `Step ${currentStep}`);
      }
    };
  }, [currentStep]);

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep >= step
                    ? 'bg-primary text-white' :'bg-accent text-muted-foreground'
                }`}
              >
                {step}
              </div>
              {step < 5 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    currentStep > step ? 'bg-primary' : 'bg-accent'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Basic Info</span>
          <span>Property</span>
          <span>Services</span>
          <span>Details</span>
          <span>Review</span>
        </div>
      </div>

      {/* Step 1: Basic Information */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="555-123-4567"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Property Address *
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="123 Main Street, City, State ZIP"
            />
            {errors.address && (
              <p className="text-red-500 text-xs mt-1">{errors.address}</p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Property Details */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Property Details</h2>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Home Size (Square Footage) *
            </label>
            <select
              value={formData.homeSize}
              onChange={(e) => handleInputChange('homeSize', e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select home size</option>
              <option value="size-2000-under">2000 sq ft and under</option>
              <option value="size-2001-3000">2001–3000 sq ft</option>
              <option value="size-3001-4000">3001–4000 sq ft</option>
              <option value="size-4001-5000">4001–5000 sq ft</option>
              <option value="size-5001-6000">5001–6000 sq ft</option>
              <option value="size-6000-plus">6000+ sq ft</option>
            </select>
            {errors.homeSize && (
              <p className="text-red-500 text-xs mt-1">{errors.homeSize}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type of Home *
            </label>
            <div className="space-y-2">
              {['Single-family', 'Townhouse', 'Other'].map((type) => (
                <label key={type} className="flex items-center">
                  <input
                    type="radio"
                    name="homeType"
                    value={type}
                    checked={formData.homeType === type}
                    onChange={(e) => handleInputChange('homeType', e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-foreground">{type}</span>
                </label>
              ))}
            </div>
            {errors.homeType && (
              <p className="text-red-500 text-xs mt-1">{errors.homeType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Number of Stories *
            </label>
            <div className="space-y-2">
              {['One', 'Two', 'Three or more'].map((story) => (
                <label key={story} className="flex items-center">
                  <input
                    type="radio"
                    name="stories"
                    value={story}
                    checked={formData.stories === story}
                    onChange={(e) => handleInputChange('stories', e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-foreground">{story}</span>
                </label>
              ))}
            </div>
            {errors.stories && (
              <p className="text-red-500 text-xs mt-1">{errors.stories}</p>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Service Selection */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Select Services</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Choose one or more services you're interested in:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service) => (
              <div
                key={service.id}
                onClick={() => handleServiceToggle(service.id)}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  formData.services.includes(service.id)
                    ? 'border-primary bg-accent' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <Icon name={service.icon} size={24} className="text-primary mr-3" />
                    <h3 className="font-semibold text-foreground">{service.name}</h3>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.services.includes(service.id)}
                    onChange={() => handleServiceToggle(service.id)}
                    className="mt-1"
                  />
                </div>
                <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
              </div>
            ))}
          </div>
          {errors.services && (
            <p className="text-red-500 text-sm mt-2">{errors.services}</p>
          )}
        </div>
      )}

      {/* Step 4: Service-Specific Questions */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Service Details</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Please provide additional details for each selected service:
          </p>
          
          {formData.services.map((serviceId) => {
            const service = services.find((s) => s.id === serviceId);
            return (
              <div key={serviceId} className="border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-3">{service?.name}</h3>
                {renderServiceSpecificQuestions(serviceId)}
              </div>
            );
          })}
        </div>
      )}

      {/* Step 5: Review & Submit */}
      {currentStep === 5 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Review Your Request</h2>
          
          <div className="bg-accent/30 rounded-lg p-6 space-y-4">
            <div>
              <h3 className="font-semibold text-foreground mb-2">Contact Information</h3>
              <p className="text-sm text-muted-foreground">
                {formData.firstName} {formData.lastName}
              </p>
              <p className="text-sm text-muted-foreground">{formData.phone}</p>
              <p className="text-sm text-muted-foreground">{formData.email}</p>
              <p className="text-sm text-muted-foreground">{formData.address}</p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">Property Details</h3>
              <p className="text-sm text-muted-foreground">Size: {formData.homeSize.replace('size-', '').replace('-', ' ')}</p>
              <p className="text-sm text-muted-foreground">Type: {formData.homeType}</p>
              <p className="text-sm text-muted-foreground">Stories: {formData.stories}</p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">Selected Services</h3>
              <div className="space-y-4">
                {formData.services.map((serviceId) => {
                  const service = services.find((s) => s.id === serviceId);
                  const answers = formData.serviceAnswers[serviceId] || {};
                  
                  return (
                    <div key={serviceId} className="border-l-4 border-primary pl-4">
                      <p className="font-medium text-foreground">{service?.name}</p>
                      
                      {/* Window Cleaning Details */}
                      {serviceId === 'window' && (
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          {answers.windowType && (
                            <li>• Type: {answers.windowType}</li>
                          )}
                          {answers.skylights && answers.skylights !== 'None' && (
                            <li>• Skylights: {answers.skylights}</li>
                          )}
                          {answers.windowStories && (
                            <li>• Stories: {answers.windowStories}</li>
                          )}
                        </ul>
                      )}
                      
                      {/* Power Washing Details */}
                      {serviceId === 'powerwashing' && (
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          {answers.powerWashAreas && answers.powerWashAreas.length > 0 && (
                            <li>• Areas: {answers.powerWashAreas.join(', ')}{answers.powerWashOther ? ` (${answers.powerWashOther})` : ''}</li>
                          )}
                          {answers.mossBuildup && (
                            <li>• Moss buildup: {answers.mossBuildup}</li>
                          )}
                        </ul>
                      )}
                      
                      {/* Roof Cleaning Details */}
                      {serviceId === 'roof' && (
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          {answers.roofType && (
                            <li>• Roof type: {answers.roofType}</li>
                          )}
                          {answers.roofPitch && (
                            <li>• Roof pitch: {answers.roofPitch}</li>
                          )}
                          {answers.roofStories && (
                            <li>• Stories: {answers.roofStories}</li>
                          )}
                        </ul>
                      )}
                      
                      {/* Gutter Cleaning Details */}
                      {serviceId === 'gutter' && (
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          {answers.gutterRoofPitch && (
                            <li>• Roof pitch: {answers.gutterRoofPitch}</li>
                          )}
                          {answers.gutterStories && (
                            <li>• Stories: {answers.gutterStories}</li>
                          )}
                        </ul>
                      )}
                      
                      {/* House Washing - No additional details */}
                      {serviceId === 'house-washing' && (
                        <p className="text-sm text-muted-foreground mt-2">Based on home size</p>
                      )}
                      
                      {/* Christmas Lights - No additional details */}
                      {serviceId === 'christmas-lights' && (
                        <p className="text-sm text-muted-foreground mt-2">Consultation required</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {formData.services.length > 0 && (
              <div className="border-t border-border pt-4 mt-4">
                <h3 className="font-semibold text-foreground mb-2">Estimated Price Range</h3>
                {(() => {
                  const { min, max, hasChristmasLights } = calculateEstimatedTotal();
                  
                  if (hasChristmasLights && formData.services.length === 1) {
                    return (
                      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                        <p className="text-sm text-foreground">
                          <strong>Christmas light installation pricing is provided after an on-site assessment to ensure accuracy and proper design.</strong> Please submit your request and we'll be happy to arrange a consultation.
                        </p>
                      </div>
                    );
                  }
                  
                  if (min > 0 && max > 0) {
                    return (
                      <>
                        <p className="text-2xl font-bold text-primary">
                          ${min} - ${max}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Pricing may vary based on access, condition, and site-specific factors.
                        </p>
                        {hasChristmasLights && (
                          <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mt-3">
                            <p className="text-xs text-foreground">
                              <strong>Note:</strong> Christmas light installation pricing will be provided after an on-site assessment.
                            </p>
                          </div>
                        )}
                      </>
                    );
                  }
                  
                  return (
                    <p className="text-sm text-muted-foreground">
                      Please complete all required questions to see your estimate.
                    </p>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Property Visit Scheduling */}
          <div className="bg-accent/30 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-3">
              Schedule Property Visit
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Please select your preferred date and time for us to visit your property and provide an accurate quote.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Preferred Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.visitDate || ''}
                    onChange={(e) => handleInputChange('visitDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-primary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white hover:border-primary/40 transition-all cursor-pointer text-foreground font-medium shadow-sm hover:shadow-md"
                    style={{
                      colorScheme: 'light',
                    }}
                  />
                  <style jsx>{`
                    input[type="date"]::-webkit-calendar-picker-indicator {
                      cursor: pointer;
                      padding: 8px;
                      border-radius: 4px;
                      transition: all 0.2s;
                    }
                    input[type="date"]::-webkit-calendar-picker-indicator:hover {
                      background-color: rgba(30, 58, 138, 0.1);
                    }
                  `}</style>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Preferred Time
                </label>
                <select
                  value={formData.visitTime || ''}
                  onChange={(e) => handleInputChange('visitTime', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-primary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white hover:border-primary/40 transition-all cursor-pointer text-foreground font-medium shadow-sm hover:shadow-md"
                >
                  <option value="">Select a time</option>
                  <option value="07:00 AM">7:00 AM</option>
                  <option value="08:00 AM">8:00 AM</option>
                  <option value="09:00 AM">9:00 AM</option>
                  <option value="10:00 AM">10:00 AM</option>
                  <option value="11:00 AM">11:00 AM</option>
                  <option value="12:00 PM">12:00 PM</option>
                  <option value="01:00 PM">1:00 PM</option>
                  <option value="02:00 PM">2:00 PM</option>
                  <option value="03:00 PM">3:00 PM</option>
                  <option value="04:00 PM">4:00 PM</option>
                  <option value="05:00 PM">5:00 PM</option>
                  <option value="06:00 PM">6:00 PM</option>
                  <option value="07:00 PM">7:00 PM</option>
                </select>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground mt-2">
              We'll contact you to confirm the appointment or suggest alternative times if needed.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={formData.additionalNotes}
              onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Any additional information or special requests..."
            />
          </div>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <p className="text-sm text-foreground">
              <strong>Please let us know what day would work best for us to visit your property and provide an accurate quote.</strong>
            </p>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        {currentStep > 1 && (
          <button
            onClick={handlePrevious}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:bg-accent transition-colors"
          >
            Previous
          </button>
        )}
        {currentStep < 5 ? (
          <button
            onClick={handleNext}
            className="ml-auto px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="ml-auto px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold"
          >
            Request My Quote
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteFormSteps;